<template>
  <div :class="['wrapper w-full md:w-180 max-w-2xl my-8 rounded-lg overflow-hidden flex flex-col']">
    <div
      ref="title"
      class="title flex justify-between items-center pl-3 pr-6 py-4 relative select-none"
    >
      <div class="flex items-center justify-center max-w-9/10">
        <span class="font-normal text-xl title-text ml-2">{{ content.title }}</span>
      </div>
      <div
        v-show="editDisabled"
        :class="[
          'edit w-8 h-4 cursor-pointer flex items-center justify-center self-start mt-2',
          isScrollInto ? 'point' : ''
        ]"
        @click="handleEdit"
      >
        {{ editStatus ? '保存' : '编辑' }}
      </div>
    </div>
    <div :class="['p-2', editStatus ? 'edit-textarea' : 'bg-transparent']">
      <textarea
        ref="textarea"
        v-model="contentDataStr"
        :readonly="!editStatus"
        :class="['textarea', editStatus ? 'edit-textarea' : 'bg-transparent']"
      ></textarea>
    </div>
  </div>
</template>

<script lang="ts">
import { Environment, SurveyRecord } from '@/store/modules/common/types';
import { Content } from '@forchange/storytime';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { SurveySaveData } from '@/components/resources/survey/baseSurvey';
import { UserAnswer } from '@/components/resources/survey/Choice/index.vue';
import { ReviewData } from '@/store/modules/survey/types';

const userModule = namespace('common');

type MultipleReview = Array<{
  manuscript: string;
  mark?: string;
  step?: number;
  questionNumber?: Array<number>;
}>;

@Component
export default class ReviewSurvey extends Vue {
  @Prop({ type: Object }) content!: Content & {
    title: string;
    manuscript: string;
    step?: number;
    mark?: string;
    questionNumber?: Array<number>;
    reviewMark?: string;
    surveyData: any;
    edit: string;
    multipleReview?: MultipleReview;
  };
  @Prop({ type: Number }) index: number;
  @Prop({ type: Boolean }) isSubmit: boolean;
  @Prop({ type: Number }) step!: Number;
  @userModule.State('surveyRecord') public surveyRecord: SurveyRecord;
  @userModule.State('authingId') public authingId: string;
  @userModule.State('version') public version: string;
  @userModule.State('environment') public environment: Environment;

  surveyData: Array<ReviewData> | undefined;
  contentDataStr = '';
  bufferHeight = 16;
  editStatus = false;

  isScrollInto = false;

  get editDisabled() {
    return !(['禁用', 'disable'].includes(this.content.edit) || this.isSubmit);
  }

  mounted() {
    this.initdata();
    this.$emit('addField', [this]);
  }

  // 获取并写入学习记录
  async initdata() {
    const surveyRecordData = this.content.surveyData;
    this.contentDataStr =
      (surveyRecordData && surveyRecordData[`${this.content.manuscript}-${this.index}`]) || '';
    if (this.contentDataStr) return;
    try {
      const storeData = await this.getSurveyDataByStore();
      const surveyData = storeData?.filter(Boolean);
      if (!surveyData || !surveyData?.length) {
        this.$Message.error({
          time: 1500,
          content: '未找到回顾内容，请检查定位信息'
        });
        return;
      }
      this.assembleContent(surveyData);
    } catch (error) {
      console.log('error: ', error);
    }
  }

  // 拼接展示
  machValue(value: SurveySaveData) {
    let content = '';
    switch (value.type) {
      case 'text':
        content = `
${(value.content as { text: string }).text}`;
        break;
      case 'choice':
        // eslint-disable-next-line no-case-declarations
        const userAnswer = value.content as UserAnswer;
        // eslint-disable-next-line no-case-declarations
        const userAnswerValue = userAnswer.picked
          ? userAnswer.pickedContent
          : userAnswer.checkedNameContent;
        content = userAnswerValue.reduce((countCheckName: string, checkName: string) => {
          return `${countCheckName}
${checkName}`;
        }, '');
        break;
      default:
        break;
    }
    return content;
  }

  // 可变teatext
  @Watch('contentDataStr')
  changeText() {
    if (!this.contentDataStr) return;
    this.$nextTick(() => {
      this.$el?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      const textarea = this.$refs?.textarea as HTMLTextAreaElement;
      textarea.style.height = '0px';
      textarea.style.height = textarea.scrollTop + textarea.scrollHeight + this.bufferHeight + 'px';
    });
  }

  // 保存提示
  handleEdit() {
    if (this.editStatus) {
      window.storyteller.updateAssignContent({
        $id: this.content.$id,
        $kind: this.content.$kind,
        ...{
          surveyData: {
            ...(this.content.surveyData || {}),
            [`${this.content.manuscript}-${this.index}`]: this.contentDataStr
          }
        }
      });
      this.$Message.success({
        time: 1500,
        content: '保存成功'
      });
      this.registerReviewData();
    }
    setTimeout(() => {
      (this.$refs.textarea as HTMLTextAreaElement).focus();
    }, 0);
    this.editStatus = !this.editStatus;
  }

  async getSurveyDataByStore() {
    let hookDatas: Array<{
      contentId: string;
      mark?: string;
      step?: number;
      changeHook: Array<Function>;
    }> = [];
    if (this.content.multipleReview) {
      this.content.multipleReview.forEach((reviewOption) => {
        hookDatas.push({
          contentId: reviewOption.manuscript,
          mark: reviewOption.mark,
          step: reviewOption.step,
          changeHook: [this.dataChange]
        });
      });
    } else {
      hookDatas = [
        {
          contentId: this.content.manuscript,
          mark: this.content.mark,
          step: this.content.step,
          changeHook: [this.dataChange]
        }
      ];
    }

    return (await this.$store.dispatch('survey/getReviewDatas', hookDatas)) as
      | Array<ReviewData>
      | undefined;
  }

  assembleContent(surveyData: Array<ReviewData>) {
    const contentData = this.matchReviewData(surveyData);

    const setContentDataStr = contentData.reduce(
      (previousValue: string, currentValue: SurveySaveData) => {
        return `${previousValue}${this.machValue(currentValue)}

    `;
      },
      ''
    );

    this.contentDataStr = setContentDataStr;
    const content = window.storyteller.topSequence?.contents.find(
      (content) => this.content.$id === content.$id && this.content.$kind === content.$kind
    ) as Content & { surveyData?: Object };
    if (!content) return;

    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        surveyData: {
          ...(content.surveyData || {}),
          [`${this.content.manuscript}-${this.index}`]: setContentDataStr
        }
      }
    });
  }

  matchReviewData(reviewDatas: Array<ReviewData>) {
    const contentData: SurveySaveData[] = [];
    reviewDatas
      .map((reviewData) => {
        if (!reviewData) return reviewData;

        if (typeof reviewData?.content === 'string') {
          const surveyAnswer = JSON.parse(reviewData.content) as {
            submit: boolean;
            data: Array<SurveySaveData>;
          };
          const surveyData = surveyAnswer.data;

          const questionNumbers = this.getQuestionNumberBycontentId(reviewData.contentId);

          if (questionNumbers) {
            contentData.push(
              ...surveyData.filter((item) => questionNumbers.includes(item.index + 1))
            );
          } else {
            contentData.push(...surveyData);
          }
        } else {
          contentData.push(reviewData.content as SurveySaveData);
        }
        return reviewData;
      })
      .filter(Boolean);
    return contentData;
  }

  getQuestionNumberBycontentId(contentId: string) {
    let questionNumber: Array<number> = [];
    if (this.content.multipleReview) {
      const targetReview = this.content.multipleReview.find(
        (item) => item.manuscript === contentId
      );
      if (!targetReview) return;
      questionNumber = targetReview.questionNumber || [];
    } else {
      questionNumber = this.content.questionNumber || [];
    }
    return questionNumber;
  }

  // 数据改变回调
  async dataChange() {
    try {
      const surveyData = await this.getSurveyDataByStore();
      if (!surveyData) return;
      this.assembleContent(surveyData);
    } catch (error) {
      console.log('error: ', error);
    }
  }

  checkSaveing() {
    this.registerReviewData();
    return this.editStatus;
  }

  followReview() {
    this.$el.scrollIntoView({ behavior: 'smooth' });
    this.isScrollInto = true;
    setTimeout(() => {
      this.isScrollInto = false;
    }, 700);
  }

  // 添加回顾支持
  registerReviewData() {
    if (!this.content.reviewMark) return;
    this.$store.dispatch('survey/setData', {
      contentId: this.content.$id.split('#')[0],
      content: {
        type: 'text',
        title: this.content.title,
        content: {
          title: '',
          text: this.contentDataStr
        }
      },
      step: this.step,
      mark: this.content.reviewMark
    });
  }
}
</script>
<style scoped>
.wrapper {
  background: #fff;
  border-color: #d3d3d3;
  @apply border;
}
.title {
  flex: 0 0 2rem;
  border-bottom: 1px solid #d3d3d3;
}

.edit {
  @apply text-motif-500;
  white-space: nowrap;
  transition: all 1s;
}

.textarea {
  @apply w-full pb-0 ring-0 border-none resize-none max-h-120;
  color: #665868;
  border-radius: 5px;
}

.edit-textarea {
  color: #716774;
  background-color: #f7f7f7;
}

.point {
  color: rgb(252, 165, 165) !important;
}
</style>
